import React, { useState, useRef } from 'react';
import ModalPortal from './ModalPortal';
import GenericAddModal from './GenericAddModal';
import DataTableDetailModal from './DataTableDetailModal';
import Tooltip from '../common/Tooltip';

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
  status: 'Active' | 'Inactive';
  created: string;
}

const DataTableCrudTest: React.FC = () => {
  const [users, setUsers] = useState<User[]>([
    { id: 1, name: 'Data 1A', email: 'Data 1B', role: 'Data 1C', status: 'Active', created: '2025-01-15' },
    { id: 2, name: 'Data 2A', email: 'Data 2B', role: 'Data 2C', status: 'Active', created: '2025-02-08' },
    { id: 3, name: 'Data 3A', email: 'Data 3B', role: 'Data 3C', status: 'Inactive', created: '2025-03-22' },
    { id: 4, name: 'Data 4A', email: 'Data 4B', role: 'Data 4C', status: 'Active', created: '2025-04-10' },
    { id: 5, name: 'Data 5A', email: 'Data 5B', role: 'Data 5C', status: 'Active', created: '2025-04-12' },
    { id: 6, name: 'Data 6A', email: 'Data 6B', role: 'Data 6C', status: 'Inactive', created: '2025-04-14' },
    { id: 7, name: 'Data 7A', email: 'Data 7B', role: 'Data 7C', status: 'Active', created: '2025-04-16' },
    { id: 8, name: 'Data 8A', email: 'Data 8B', role: 'Data 8C', status: 'Inactive', created: '2025-04-18' },
    { id: 9, name: 'Data 9A', email: 'Data 9B', role: 'Data 9C', status: 'Active', created: '2025-04-20' },
    { id: 10, name: 'Data 10A', email: 'Data 10B', role: 'Data 10C', status: 'Active', created: '2025-04-22' },
    { id: 11, name: 'Data 11A', email: 'Data 11B', role: 'Data 11C', status: 'Inactive', created: '2025-04-24' },
    { id: 12, name: 'Data 12A', email: 'Data 12B', role: 'Data 12C', status: 'Active', created: '2025-04-26' },
    { id: 13, name: 'Data 13A', email: 'Data 13B', role: 'Data 13C', status: 'Active', created: '2025-04-28' },
    { id: 14, name: 'Data 14A', email: 'Data 14B', role: 'Data 14C', status: 'Inactive', created: '2025-04-30' },
    { id: 15, name: 'Data 15A', email: 'Data 15B', role: 'Data 15C', status: 'Active', created: '2025-05-02' }
  ]);

  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState<number | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8; // Show 8 rows per page to trigger vertical scroll

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination calculations
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageUsers = filteredUsers.slice(startIndex, endIndex);

  // Reset to page 1 when search changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);  const handleAddNew = (data: Record<string, string>) => {
    const user: User = {
      id: Math.max(...users.map(u => u.id)) + 1,
      name: `Item ${Math.max(...users.map(u => u.id)) + 1}`,
      email: `item${Math.max(...users.map(u => u.id)) + 1}@example.com`,
      role: data.label2,
      status: data.label4 as 'Active' | 'Inactive',
      created: data.label3
    };
    setUsers([...users, user]);
  };

  const handleDeleteUser = (id: number) => {
    setUsers(users.filter(u => u.id !== id));
    setShowDeleteModal(null);
  };

  const [tooltipVisible, setTooltipVisible] = useState(false);
  const infoAnchorRef = useRef<HTMLButtonElement | null>(null);

  return (
    <div className="layout-data-table-pattern">
      <div className="css-info">
        <div>Data Table CRUD Pattern</div>
        <button
          ref={infoAnchorRef}
          className="css-info-icon"
          aria-label="CSS info"
          title="CSS info"
          aria-expanded={tooltipVisible}
          aria-controls="css-tooltip-datatable"
          onClick={() => setTooltipVisible(v => !v)}
        >
          <span className="sr-only">CSS info</span>
        </button>
  <Tooltip anchorRef={infoAnchorRef} isOpen={tooltipVisible} onClose={() => setTooltipVisible(false)} id="css-tooltip-datatable">
          <strong>CSS Structure for Data Table + Scroll + Pagination:</strong>
          
          <div style={{ marginTop: '8px', marginBottom: '12px', padding: '8px', background: '#f8f9fa', borderRadius: '4px', fontSize: '11px', lineHeight: '1.4' }}>
            <strong>Layout Pattern:</strong> .layout-data-table-pattern<br/>
            ├── .container-toolbar.layout-flex-fixed (search/actions - fixed height)<br/>
            ├── .container-data-table.layout-flex-content.layout-flex-column<br/>
            │   ├── .layout-table-scroll-container.layout-flex-content (scrollable table)<br/>
            │   │   └── .table-data.layout-table-scrollable (actual table)<br/>
            │   └── .layout-pagination-container.layout-flex-fixed (pagination - fixed height)<br/>
            <br/>
            <strong>Key CSS Classes:</strong><br/>
            • .layout-data-table-pattern: Flex column container for entire table pattern<br/>
            • .layout-flex-content: Takes available space (table area)<br/>
            • .layout-flex-fixed: Fixed height areas (toolbar + pagination)<br/>
            • .layout-table-scroll-container: Scrollable container with custom scrollbar<br/>
            • .layout-pagination-container: Fixed footer with pagination controls<br/>
            • .interactive-table-row: Hoverable table rows with click handlers<br/>
            <br/>
            <strong>Flex Behavior:</strong><br/>
            • min-height: 0 on flex items allows proper flex shrinking<br/>
            • Table scrolls vertically when content exceeds container height<br/>
            • Pagination stays visible at bottom, toolbar at top<br/>
            • Responsive to different container sizes and data amounts
          </div>
          
          <strong>Additional CSS Classes Used:</strong><br/>
          .container-toolbar - Search and action toolbar<br/>
          .control-search-field - Search input styling<br/>
          .btn-primary, .btn-secondary - Action buttons<br/>
          .container-data-table - Table container<br/>
          .table-data - Data table styling<br/>
          .visual-status-success/inactive - Status badges<br/>
          .modal-overlay, .modal-content - Modal dialogs
        </Tooltip>
      </div>

      {/* Toolbar */}
      <div className="container-toolbar layout-flex-fixed">
        <div className="layout-flex-row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="layout-flex-row" style={{ gap: '12px' }}>
            <div className="control-search-field">
              <input 
                type="text" 
                placeholder="Search users..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button className="btn-secondary interactive-hover">
              🔽 Filter
            </button>
          </div>
          <button 
            className="btn-primary interactive-hover"
            onClick={() => setShowAddModal(true)}
          >
            ➕ Add New
          </button>
        </div>
      </div>

      {/* Data Table Container - Takes available space */}
      <div className="container-data-table layout-flex-content layout-flex-column">
        {/* Table with scrolling */}
        <div className="layout-table-scroll-container layout-flex-content">
          <table className="table-data layout-table-scrollable">
            <thead>
              <tr>
                <th>Table-Head 1</th>
                <th>Table-Head 2</th>
                <th>Table-Head 3</th>
                <th>Table-Head 4</th>
                <th>Table-Head 5</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentPageUsers.map((user, index) => (
                <tr key={user.id} className="interactive-table-row" onClick={() => setSelectedUser(user)}>
                  <td className="text-body">{user.name}</td>
                  <td className="text-body">{user.email}</td>
                  <td className="text-body">{user.role}</td>
                  <td>
                    <span className={`visual-status-${user.status === 'Active' ? '1' : '2'}`}>
                      Status {user.status === 'Active' ? '1' : '2'}
                    </span>
                  </td>
                  <td className="text-body">{user.created}</td>
                  <td>
                    <div className="layout-flex-row" style={{ gap: '8px' }}>
                      <button 
                        className="btn-ghost-small interactive-hover"
                        onClick={() => setShowDeleteModal(user.id)}
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls - Fixed footer */}
        <div className="layout-pagination-container layout-flex-fixed">
          <div className="layout-pagination-info text-caption">
            Showing {startIndex + 1}-{Math.min(endIndex, filteredUsers.length)} of {filteredUsers.length} entries
          </div>
          
          <div className="layout-pagination-controls">
            <button 
              className="layout-pagination-button"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
            >
              Previous
            </button>
            
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
              <button
                key={pageNum}
                className={`layout-pagination-button ${currentPage === pageNum ? 'layout-pagination-button--active' : ''}`}
                onClick={() => setCurrentPage(pageNum)}
              >
                {pageNum}
              </button>
            ))}
            
            <button 
              className="layout-pagination-button"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Add New Modal */}
      <GenericAddModal 
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleAddNew}
        title="Add New Item"
      />

      {/* Delete Confirmation Modal */}
            {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <ModalPortal isOpen={true} onClose={() => setShowDeleteModal(null)}>
          <div className="modal-overlay">
            <div className="modal-content modal-content--small">
              <div className="modal-header">
                <h3 className="text-title-small">Confirm Delete</h3>
              </div>
              <div className="modal-body">
                <p className="text-body">Are you sure you want to delete this user?</p>
              </div>
              <div className="modal-footer">
                <button 
                  className="visual-button-secondary" 
                  onClick={() => setShowDeleteModal(null)}
                >
                  Cancel
                </button>
                <button 
                  className="visual-button-danger" 
                  onClick={() => {
                    setUsers(users.filter(u => u.id !== showDeleteModal));
                    setShowDeleteModal(null);
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </ModalPortal>
      )}

      {/* Row Detail Modal */}
      <DataTableDetailModal
        isOpen={!!selectedUser}
        onClose={() => setSelectedUser(null)}
        selectedUser={selectedUser}
      />
    </div>
  );
};

export default DataTableCrudTest;